import { router, useLocalSearchParams } from 'expo-router';
import { useCallback, useState } from 'react';

import { useResendOtpV2Mutation } from '@fhs-legacy/frontend/src/generated/graphql-gateway';

import { useAuthRouteBase } from '../auth-route-base-context';

import { useCRMEventsContext, useToast } from '.';

export const useResendOtp = () => {
  const [resendOtpLoading, setResendOtpLoading] = useState<boolean>(false);
  const [resendCountdownMessage, setResendCountdownMessage] = useState<string>('');
  const { loginJwt } = useLocalSearchParams<{ loginJwt?: string }>();
  const { paths } = useAuthRouteBase();
  const { logResendEvent } = useCRMEventsContext();
  const { show } = useToast();
  const onError = useCallback(
    err => {
      if (err?.graphQLErrors && err.graphQLErrors.length > 0) {
        //Take the highest level error code
        const code = err.graphQLErrors[0].extensions.code;
        switch (code) {
          case 'NO_VALID_CODES':
            router.dismissTo(paths.signIn);
            show({
              text: "You've exceeded the limit of attempts. Please request a new code and try again.",
              variant: 'informative',
            });
            break;
          case 'TOO_MANY_REQUESTS':
            show({
              text: 'You have exceeded the limit of attempts. Please try again later.',
              variant: 'informative',
            });
            break;
          default:
            router.dismissTo(paths.signIn);
            show({ text: 'Oops, something went wrong. Please try again', variant: 'informative' });
        }
      } else {
        show({ text: 'Oops, something went wrong. Please try again', variant: 'informative' });
      }
    },
    [paths.signIn, show]
  );
  const onResendOtp = useCallback((data, _clientOption) => {
    return data.resendOtpV2;
  }, []);
  const [internalResendOtp] = useResendOtpV2Mutation({
    onCompleted: onResendOtp,
    onError,
  });
  const resendOtp = useCallback(async () => {
    if (!loginJwt) {
      show({ text: 'Invalid request. Please try again.', variant: 'informative' });
      return;
    }
    setResendOtpLoading(true);
    try {
      const result = await internalResendOtp({
        variables: {
          input: {
            loginJwt,
          },
        },
      });
      logResendEvent({});

      if (result) {
        show({ text: 'Verification Code Successfully Resent!', variant: 'neutral' });
      }
      const countDown = (counter: number) => {
        if (counter === 0) {
          setResendCountdownMessage('');
          return;
        }
        setResendCountdownMessage(`You can request a new code in ${counter} seconds.`);
        setTimeout(() => {
          countDown(counter - 1);
        }, 1000);
      };
      setResendCountdownMessage('You can request a new code in 60 seconds.');
      countDown(60);
      setTimeout(() => {
        setResendOtpLoading(false);
      }, 60000);
    } catch (e) {
      // log error?
    } finally {
      setResendOtpLoading(false);
    }
  }, [internalResendOtp, logResendEvent, loginJwt, show]);
  return {
    resendOtp,
    resendOtpLoading,
    resendCountdownMessage,
  };
};
